import React, { useState, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { getDashboardStats } from "../api/dashboardApi";
import { saveAppointment, patchAppointment, getAppointmentsByDateRange } from "../api/appointmentApi";
import DayNavigator from "../components/Dashboard/DayNavigator";
import NextAppointmentCard from "../components/Dashboard/NextAppointmentCard";
import PendingApprovalStrip from "../components/Dashboard/PendingApprovalStrip";
import PendingApprovalsModal from "../components/Dashboard/PendingApprovalsModal";
import PastConfirmedStrip from "../components/Dashboard/PastConfirmedStrip";
import PastConfirmedModal from "../components/Dashboard/PastConfirmedModal";
import { usePastConfirmedAppointments } from "../hooks/usePastConfirmedAppointments";
import TodayTimeline from "../components/Dashboard/TodayTimeline";
import StatCard from "../components/Dashboard/StatCard";
import BookingPageCard from "../components/Dashboard/BookingPageCard";
import DashboardEmptyState from "../components/Dashboard/DashboardEmptyState";
import QuickActions from "../components/Dashboard/QuickActions";
import Modal from "../components/Shared/Modal";
import AppointmentForm from "../components/Appointment/AppointmentForm";
import ViewAppointment from "../components/Appointment/ViewAppointment";
import CancelAppointmentForm from "../components/Appointment/CancelAppointmentForm";
import ContactClient from "../components/Client/ContactClient";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";

import OnboardingDashboard from "../components/Dashboard/OnboardingDashboard";
import { getSetupProgress } from "../api/setupApi";
import { useBookingPage } from "../hooks/useBookingPage";
import { FORM_MODE } from "../config/constants";
import { useToast } from "../hooks/useToast";
import { useUserPreferences } from "../hooks/useUserPreferences";
import { getBookingUrl } from "../utility/environment";
import usePageVisibility from "../hooks/usePageVisibility";
import useCreateIntent from "../hooks/useCreateIntent";
import { formatCompactCurrency, parseDecimal128 } from "../utility/priceUtils";

const DashboardPage = () => {
  const [showAppointmentDrawer, setShowAppointmentDrawer] = useState(false);
  const [showAppointmentView, setShowAppointmentView] = useState(false);
  const [showCancelDrawer, setShowCancelDrawer] = useState(false);
  const [showContactDrawer, setShowContactDrawer] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showPastConfirmedModal, setShowPastConfirmedModal] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [viewSource, setViewSource] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentFormMode, setAppointmentFormMode] = useState(null);
  const [appointmentModalTitle, setAppointmentModalTitle] = useState("Create Appointment");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const modalCloseRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { currency, firstName } = useUserPreferences();
  const { pastConfirmedAppointments, isError: isPastConfirmedError } =
    usePastConfirmedAppointments({ paused: bulkProgress != null });

  // Mobile bottom-nav "+" deep-links here with ?new=appointment
  useCreateIntent("appointment", () => {
    setShowAppointmentDrawer(true);
    setAppointmentFormMode(FORM_MODE.CREATE);
    setAppointmentModalTitle("Create Appointment");
  });

  const getGreeting = () => {
    const hour = DateTime.now().hour;
    if (hour >= 5 && hour <= 11) return "Good morning";
    if (hour >= 12 && hour <= 16) return "Good afternoon";
    return "Good evening";
  };

  const isToday = DateTime.fromJSDate(selectedDate).hasSame(DateTime.now(), "day");

  const goToPreviousDay = () => {
    setSelectedDate((prev) => DateTime.fromJSDate(prev).minus({ days: 1 }).toJSDate());
  };

  const goToNextDay = () => {
    setSelectedDate((prev) => DateTime.fromJSDate(prev).plus({ days: 1 }).toJSDate());
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Setup progress
  const { data: setupProgressResponse, isLoading: isSetupLoading } = useQuery({
    queryKey: ["setupProgress"],
    queryFn: getSetupProgress,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });

  const setupProgress = setupProgressResponse?.data;
  const isSetupComplete = setupProgress?.isSetupComplete || false;
  const isPageVisible = usePageVisibility();

  // Dashboard stats
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ["dashboardStats"],
    queryFn: getDashboardStats,
    refetchInterval: isPageVisible ? 60000 : false,
    refetchOnWindowFocus: true,
    enabled: isPageVisible,
    placeholderData: keepPreviousData,
  });

  // Booking page data
  const { data: bookingPageResponse, isLoading: isBookingPageLoading } =
    useBookingPage();
  const bookingPageData = bookingPageResponse?.data;

  // Selected-day appointments (only fetches when viewing a non-today date)
  const selectedDayStart = DateTime.fromJSDate(selectedDate).startOf("day").toISO();
  const selectedDayEnd = DateTime.fromJSDate(selectedDate).endOf("day").toISO();

  const { data: selectedDayData, isLoading: isLoadingSelectedDay } = useQuery({
    queryKey: [
      "appointments",
      { start: selectedDayStart, end: selectedDayEnd, page: 1, limit: 50 },
    ],
    queryFn: () =>
      getAppointmentsByDateRange({
        start: selectedDayStart,
        end: selectedDayEnd,
        page: 1,
        limit: 50,
      }),
    enabled: !isToday,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });

  const displayedAppointments = isToday
    ? dashboardData?.todayAppointments || []
    : selectedDayData?.appointments || [];
  const isLoadingAppointments = isToday ? isLoading : isLoadingSelectedDay;

  const {
    mutate: mutatePatchAppointment,
    mutateAsync: mutatePatchAppointmentAsync,
  } = useMutation({
    mutationFn: patchAppointment,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["dashboardStats"] });
      await queryClient.cancelQueries({ queryKey: ["appointments"] });

      const previousDashboard = queryClient.getQueryData(["dashboardStats"]);
      const previousAppointmentQueries = queryClient.getQueriesData({ queryKey: ["appointments"] });

      const newStatus = variables.data?.status;
      if (newStatus && previousDashboard) {
        queryClient.setQueryData(["dashboardStats"], (old) => {
          if (!old) return old;
          const updateStatus = (apt) =>
            apt.id === variables.id ? { ...apt, status: newStatus } : apt;
          const shouldRemoveFromPending = newStatus === "confirmed" || newStatus === "cancelled";
          return {
            ...old,
            todayAppointments: (old.todayAppointments || []).map(updateStatus),
            pendingAppointments: shouldRemoveFromPending
              ? (old.pendingAppointments || []).filter((apt) => apt.id !== variables.id)
              : (old.pendingAppointments || []).map(updateStatus),
          };
        });
      }

      // Update any cached appointment date-range queries
      if (newStatus) {
        previousAppointmentQueries.forEach(([queryKey]) => {
          queryClient.setQueryData(queryKey, (old) => {
            if (!old?.appointments) return old;
            return {
              ...old,
              appointments: old.appointments.map((apt) =>
                apt.id === variables.id ? { ...apt, status: newStatus } : apt
              ),
            };
          });
        });
      }

      return { previousDashboard, previousAppointmentQueries };
    },
    onSuccess: (data, variables) => {
      if (variables.data?.status === "cancelled") {
        toast.warning(
          "Appointment Cancelled",
          "Appointment has been cancelled successfully"
        );
      } else if (variables.data?.status === "confirmed") {
        toast.success(
          "Appointment Approved",
          "Appointment has been approved successfully"
        );
      } else {
        toast.success(
          "Appointment Updated",
          "Appointment has been updated successfully"
        );
      }

      closeDrawerHandler();
    },
    onError: (err, variables, context) => {
      // Rollback optimistic updates
      if (context?.previousDashboard) {
        queryClient.setQueryData(["dashboardStats"], context.previousDashboard);
      }
      if (context?.previousAppointmentQueries) {
        context.previousAppointmentQueries.forEach(([queryKey, data]) => {
          if (data !== undefined) queryClient.setQueryData(queryKey, data);
        });
      }
      // Inline callers (approve/decline) surface a toast here.
      // Form callers use mutatePatchAppointmentAsync and handle errors locally.
      const errorMessage =
        err.response?.data?.error?.message ||
        "An error occurred while updating the appointment";
      toast.error("Operation Failed", errorMessage);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboardStats"] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });

  const { mutateAsync: mutateSaveAppointment } = useMutation({
    mutationFn: saveAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboardStats"] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      toast.success(
        "Appointment Created",
        "New appointment has been scheduled successfully"
      );
      closeDrawerHandler();
    },
  });

  const closeDrawerHandler = () => {
    setShowAppointmentDrawer(false);
    setShowAppointmentView(false);
    setShowCancelDrawer(false);
    setShowContactDrawer(false);
    setViewSource(null);
    setSelectedAppointment(null);
    setAppointmentFormMode(null);
    setIsFormDirty(false);
  };

  const onViewHandler = (appointment) => {
    setViewSource(null);
    setShowAppointmentView(true);
    setSelectedAppointment(appointment);
  };

  const onEditHandler = (appointment) => {
    setShowAppointmentDrawer(true);
    setAppointmentFormMode(FORM_MODE.UPDATE);
    setAppointmentModalTitle("Edit Appointment");
    setSelectedAppointment(appointment);
  };

  const onViewFromPendingHandler = (appointment) => {
    setShowPendingModal(false);
    setViewSource("pending");
    setShowAppointmentView(true);
    setSelectedAppointment(appointment);
  };

  const onBackToPendingHandler = useCallback(() => {
    setShowAppointmentView(false);
    setSelectedAppointment(null);
    setViewSource(null);
    setShowPendingModal(true);
  }, []);

  const onEditFromPendingHandler = (appointment) => {
    setShowPendingModal(false);
    onEditHandler(appointment);
  };

  const onCancelHandler = (appointment) => {
    setShowCancelDrawer(true);
    setSelectedAppointment(appointment);
  };

  const onContactHandler = (appointment) => {
    setShowContactDrawer(true);
    setSelectedAppointment(appointment);
  };

  const onApproveHandler = (appointmentId) => {
    mutatePatchAppointment({
      id: appointmentId,
      data: { status: "confirmed" },
      shouldNotifyClient: true,
    });
  };

  const onDeclineHandler = (appointmentId, { isPast } = {}) => {
    mutatePatchAppointment({
      id: appointmentId,
      data: { status: "cancelled" },
      shouldNotifyClient: !isPast,
    });
  };

  const onUpdateStatusHandler = (appointmentId, status) => {
    mutatePatchAppointment({
      id: appointmentId,
      data: { status },
    });
  };

  const onMarkCompletedHandler = (appointmentId) => {
    mutatePatchAppointment({ id: appointmentId, data: { status: "completed" } });
  };

  const onMarkNoShowHandler = (appointmentId) => {
    mutatePatchAppointment({
      id: appointmentId,
      data: { status: "cancelled" },
      shouldNotifyClient: false,
    });
  };

  const onMarkAllCompletedHandler = async (ids) => {
    setBulkProgress({ done: 0, total: ids.length });
    const failedIds = [];
    await Promise.all(
      ids.map((id) =>
        patchAppointment({ id, data: { status: "completed" } })
          .then(() => setBulkProgress((p) => ({ ...p, done: p.done + 1 })))
          .catch(() => {
            failedIds.push(id);
          })
      )
    );
    queryClient.invalidateQueries({ queryKey: ["appointments"] });
    queryClient.invalidateQueries({ queryKey: ["dashboardStats"] });
    setBulkProgress(null);
    if (failedIds.length > 0) {
      toast.error(
        "Some updates failed",
        `${failedIds.length} of ${ids.length} appointment${ids.length === 1 ? "" : "s"} could not be updated. Please try again.`
      );
    }
  };

  const onClientClickHandler = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const onCreateAppointmentHandler = () => {
    setShowAppointmentDrawer(true);
    setAppointmentFormMode(FORM_MODE.CREATE);
    setAppointmentModalTitle("Create Appointment");
  };

  const onShareBookingLinkHandler = () => {
    const slug = bookingPageData?.slug;
    if (!slug) {
      navigate("/booking-page#booking-link");
      return;
    }
    const bookingUrl = getBookingUrl(slug);
    if (navigator.share) {
      navigator.share({ title: "Book an appointment", url: bookingUrl }).catch(() => {});
    } else {
      navigator.clipboard
        .writeText(bookingUrl)
        .then(() =>
          toast.success("Link Copied", "Booking link copied to clipboard")
        )
        .catch(() =>
          toast.error("Copy Failed", "Could not copy link to clipboard")
        );
    }
  };

  // Dashboard stat cards use the compact, no-cents format. Use formatPriceWithCurrency
  // directly for any full-precision number (line items, totals).
  const formatCompactStat = (amount) => formatCompactCurrency(amount, currency);

  // Compute today's revenue from completed/confirmed appointments
  const todayRevenue = (dashboardData?.todayAppointments || []).reduce(
    (sum, a) => {
      if (a.status === "completed" || a.status === "confirmed") {
        return sum + (parseDecimal128(a.totalPrice) || 0);
      }
      return sum;
    },
    0
  );

  // Show dedicated onboarding dashboard when setup is incomplete
  if (!isSetupLoading && !isSetupComplete && setupProgress) {
    return <OnboardingDashboard setupProgress={setupProgress} />;
  }

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="space-y-6 page-enter">
          {/* Welcome Greeting */}
          <div className="reveal-on-enter">
            <p className="smallcaps text-ink-400 mb-1">
              {DateTime.fromJSDate(selectedDate).toFormat("EEEE · d LLL")}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-ink-900 tracking-display">
              {getGreeting()}{firstName ? `, ${firstName}` : ""}.
            </h1>
          </div>

          {/* Day Navigator + Quick Actions */}
          <div className="flex items-center justify-between reveal-on-enter" style={{ "--reveal-delay": "60ms" }}>
            <DayNavigator
              selectedDate={selectedDate}
              isToday={isToday}
              onPrevious={goToPreviousDay}
              onNext={goToNextDay}
              onToday={goToToday}
            />
            <QuickActions
              onCreateAppointment={onCreateAppointmentHandler}
              onShareBookingLink={onShareBookingLinkHandler}
            />
          </div>

          {/* Pending Approvals Strip — above hero card for urgency */}
          <div className="reveal-on-enter" style={{ "--reveal-delay": "120ms" }}>
            <PendingApprovalStrip
              appointments={dashboardData?.pendingAppointments}
              onReviewClick={() => setShowPendingModal(true)}
            />
          </div>

          {/* Past Confirmed Strip — lower urgency, below pending */}
          <PastConfirmedStrip
            count={isPastConfirmedError ? 0 : pastConfirmedAppointments.length}
            onReviewClick={() => setShowPastConfirmedModal(true)}
          />

          {/* Next Up Hero Card — only when viewing today AND there are appointments.
              When today is empty, DashboardEmptyState (below) owns the message. */}
          {isToday && !isLoading && displayedAppointments.length > 0 && (
            <div className="reveal-on-enter" style={{ "--reveal-delay": "180ms" }}>
            <NextAppointmentCard
              todayAppointments={dashboardData?.todayAppointments || []}
              isLoading={isLoading}
              currency={currency}
              onView={onViewHandler}
              onEdit={onEditHandler}
              onCancel={onCancelHandler}
              onContact={onContactHandler}
              onUpdateStatus={onUpdateStatusHandler}
              onClientClick={onClientClickHandler}
            />
            </div>
          )}

          {/* Two-column layout: Timeline + Sidebar */}
          <div className="lg:grid lg:grid-cols-12 lg:gap-6 reveal-on-enter" style={{ "--reveal-delay": "240ms" }}>
            {/* Main column: Day Timeline (or empty-state hero on today's empty view) */}
            <div className="lg:col-span-8">
              {isToday && !isLoadingAppointments && displayedAppointments.length === 0 ? (
                <DashboardEmptyState
                  onShareBookingLink={onShareBookingLinkHandler}
                  onCreateAppointment={onCreateAppointmentHandler}
                />
              ) : (
                <TodayTimeline
                  appointments={displayedAppointments}
                  isLoading={isLoadingAppointments}
                  selectedDate={selectedDate}
                  onView={onViewHandler}
                  onEdit={onEditHandler}
                  onCancel={onCancelHandler}
                  onContact={onContactHandler}
                  onUpdateStatus={onUpdateStatusHandler}
                  onClientClick={onClientClickHandler}
                />
              )}
            </div>

            {/* Sidebar: Stats + Booking Page */}
            <div className="lg:col-span-4 mt-6 lg:mt-0 space-y-4">
              {/* Stat Cards */}
              <div className="grid grid-cols-3 lg:grid-cols-1 gap-3 lg:gap-4">
                <StatCard
                  title="Today"
                  value={formatCompactStat(todayRevenue)}
                  isLoading={isLoading}
                />
                <StatCard
                  title="This Week"
                  value={dashboardData?.weekAppointmentsCount ?? 0}
                  isLoading={isLoading}
                  footnote={
                    dashboardData?.weekCancelledCount > 0
                      ? `${dashboardData.weekCancelledCount} cancelled`
                      : undefined
                  }
                />
                <StatCard
                  title="Month"
                  value={formatCompactStat(dashboardData?.monthlyRevenue || 0)}
                  isLoading={isLoading}
                />
              </div>

              {/* Booking Page Card */}
              <BookingPageCard
                bookingPageData={bookingPageData}
                isLoading={isBookingPageLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAppointmentDrawer && (
        <Modal
          open={showAppointmentDrawer}
          onClose={closeDrawerHandler}
          title={appointmentModalTitle}
          size="lg"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
        >
          <AppointmentForm
            closeDrawerHandler={closeDrawerHandler}
            defaultAppointment={selectedAppointment}
            defaultClient={{
              _id: selectedAppointment?.clientId,
              fullName: selectedAppointment?.clientFullName,
            }}
            mode={appointmentFormMode}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
            saveAppointment={mutateSaveAppointment}
            patchAppointment={mutatePatchAppointmentAsync}
            onModalTitleChange={setAppointmentModalTitle}
          />
        </Modal>
      )}

      {showAppointmentView && (
        <Modal
          open={showAppointmentView}
          onClose={viewSource === "pending" ? onBackToPendingHandler : closeDrawerHandler}
          title="Appointment Details"
          size="sm"
        >
          {viewSource === "pending" && (
            <button
              type="button"
              onClick={onBackToPendingHandler}
              className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 transition-colors mb-3 -mt-1"
            >
              <ArrowLeftIcon className="size-3.5" />
              Back to Pending Approvals
            </button>
          )}
          <ViewAppointment
            appointment={selectedAppointment}
            clientName={selectedAppointment?.clientFullName}
          />
        </Modal>
      )}

      {showCancelDrawer && (
        <Modal
          open={showCancelDrawer}
          onClose={closeDrawerHandler}
          title="Cancel Appointment"
          size="sm"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
        >
          <CancelAppointmentForm
            appointment={selectedAppointment}
            closeDrawerHandler={closeDrawerHandler}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
            patchAppointment={mutatePatchAppointmentAsync}
          />
        </Modal>
      )}

      {showContactDrawer && (
        <Modal
          open={showContactDrawer}
          onClose={closeDrawerHandler}
          title="Contact Client"
          size="sm"
        >
          <ContactClient client={selectedAppointment} />
        </Modal>
      )}

      {showPendingModal && (
        <PendingApprovalsModal
          open={showPendingModal}
          onClose={() => setShowPendingModal(false)}
          appointments={dashboardData?.pendingAppointments || []}
          onApprove={onApproveHandler}
          onDecline={onDeclineHandler}
          onView={onViewFromPendingHandler}
          onEdit={onEditFromPendingHandler}
          onClientClick={onClientClickHandler}
        />
      )}

      {showPastConfirmedModal && (
        <PastConfirmedModal
          open={showPastConfirmedModal}
          onClose={() => setShowPastConfirmedModal(false)}
          appointments={pastConfirmedAppointments}
          onMarkCompleted={onMarkCompletedHandler}
          onMarkNoShow={onMarkNoShowHandler}
          onClientClick={onClientClickHandler}
          onMarkAllCompleted={onMarkAllCompletedHandler}
          bulkProgress={bulkProgress}
        />
      )}
    </>
  );
};

export default DashboardPage;
